import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useGetAllBankInfoQuery } from "../../../../store/admin/settings/societyBankSlice";
import { useUpdateContributionCategoryMutation } from "../../../../store/admin/contribution";
import toast from "react-hot-toast";
import { Input } from "../../../../components/Ui/new-input";
import CustomSelect from "../../../../components/custom-select/custom-select";
import { customStyles } from "../../../../components/custom-select/custom-styles";

const CATEGORY_KIND_OPTIONS = [
  { label: "Savings (withdrawable)", value: "savings" },
  { label: "Shares (non-withdrawable)", value: "shares" },
  { label: "Other", value: "other" },
];

function withdrawableForKind(kind) {
  if (kind === "savings") return 1;
  if (kind === "shares") return 0;
  return null;
}

export default function EditContributionModal({ previousData, handleClose }) {
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedWithdrawableStatus, setSelectedWithdrawableStatus] =
    useState(null);
  const [selectedKind, setSelectedKind] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      category_kind: "other",
      is_withdrawable: "",
      company_bank_id: "",
      threshold: "",
      end_date: "",
      start_date: "",
    },
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Contribution name is required"),
      company_bank_id: Yup.string().required("Payment bank is required"),
      category_kind: Yup.string()
        .oneOf(["savings", "shares", "other"], "Invalid category type")
        .required("Category type is required"),
      end_date: Yup.date()
        .nullable()
        .test(
          "future-date",
          "End date must be greater than or equal to start date",
          function (value) {
            if (!value) return true;
            const startDate = this.parent.start_date;
            if (startDate && moment(value).isBefore(moment(startDate), "day")) {
              return false;
            }
            return true;
          }
        ),
      start_date: Yup.date().nullable(),
      threshold: Yup.number().nullable().typeError("Threshold must be a number"),
      is_withdrawable: Yup.number()
        .nullable()
        .oneOf([0, 1], "Invalid withdrawable status"),
    }),
  });

  useEffect(() => {
    if (previousData) {
      const kind =
        previousData?.category_kind ||
        (previousData?.is_withdrawable ? "savings" : "other");
      formik.setValues({
        id: previousData?.id,
        name: previousData?.name || "",
        threshold: previousData?.amount_per_individuals || "",
        company_bank_id: previousData?.company_bank_id || "",
        end_date: previousData?.end_date || "",
        start_date: previousData?.start_date || "",
        is_withdrawable: previousData?.is_withdrawable ?? "",
        category_kind: kind,
      });
      setSelectedKind(
        CATEGORY_KIND_OPTIONS.find((o) => o.value === kind) ||
          CATEGORY_KIND_OPTIONS[2]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousData]);

  const [handleUpdateContributionCategory, { isLoading }] =
    useUpdateContributionCategoryMutation();
  const { data } = useGetAllBankInfoQuery({ pageNumber: 1 });

  const formattedCompanyBanks = data?.company_banks?.map((item) => {
    return {
      label: item.bank_name + " | " + item.account_name,
      value: item.bank_id,
    };
  });

  useEffect(() => {
    if (previousData?.company_bank_id && formattedCompanyBanks) {
      const selectedOption = formattedCompanyBanks?.find(
        (option) =>
          String(option.value) === String(previousData.company_bank_id)
      );
      setSelectedBank(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousData, data]);

  useEffect(() => {
    if (previousData) {
      const forced = withdrawableForKind(previousData.category_kind);
      const withdrawable =
        forced !== null ? forced : previousData.is_withdrawable ? 1 : 0;
      setSelectedWithdrawableStatus(
        withdrawable
          ? { label: "Withdrawable", value: 1 }
          : { label: "Non-Withdrawable", value: 0 }
      );
    }
  }, [previousData]);

  const kindLocksWithdrawable =
    formik.values.category_kind === "savings" ||
    formik.values.category_kind === "shares";
  const isSystemCategory = Boolean(previousData?.is_system);

  const handleKindChange = (option) => {
    if (isSystemCategory) return;
    const kind = option?.value || "other";
    setSelectedKind(option);
    formik.setFieldValue("category_kind", kind);
    formik.setFieldTouched("category_kind", true);
    const forced = withdrawableForKind(kind);
    if (forced !== null) {
      formik.setFieldValue("is_withdrawable", forced);
      setSelectedWithdrawableStatus(
        forced
          ? { label: "Withdrawable", value: 1 }
          : { label: "Non-Withdrawable", value: 0 }
      );
    }
  };

  const handleFormSubmit = async (formikValues) => {
    const kind = formikValues.category_kind || "other";
    const forced = withdrawableForKind(kind);

    const updatedValues = {
      name: formikValues.name,
      company_bank_id: parseInt(formikValues.company_bank_id),
      category_kind: kind,
    };

    if (formikValues.start_date && formikValues.start_date !== "") {
      updatedValues.start_date = moment(formikValues.start_date).format(
        "YYYY-MM-DD"
      );
    } else {
      updatedValues.start_date = null;
    }

    if (formikValues.end_date && formikValues.end_date !== "") {
      updatedValues.end_date = moment(formikValues.end_date).format(
        "YYYY-MM-DD"
      );
    } else {
      updatedValues.end_date = null;
    }

    if (forced !== null) {
      updatedValues.is_withdrawable = forced;
    } else if (
      formikValues.is_withdrawable !== "" &&
      formikValues.is_withdrawable !== null &&
      formikValues.is_withdrawable !== undefined
    ) {
      updatedValues.is_withdrawable = parseInt(formikValues.is_withdrawable);
    } else {
      updatedValues.is_withdrawable = 0;
    }

    const response = await handleUpdateContributionCategory({
      id: formikValues.id,
      ...updatedValues,
    });

    if ("error" in response) {
      const { data } = response.error;
      return toast.error(data.message || "Failed to update contribution");
    }

    const { message, status } = response.data;
    if (!status) return toast.error(message);
    handleClose();
    toast.success(message);
    formik.resetForm();
  };

  const branchName = previousData?.branch?.name;

  return (
    <div>
      <h3 className="mb-2 text-black1 text-xl pt-2 fw-bold text-center">
        Edit Contribution
      </h3>
      {isSystemCategory && (
        <p className="text-center text-sm text-blacklight px-3 mb-2">
          System default category — you can rename it, but type and delete are
          protected.
        </p>
      )}
      <form className="w-full" onSubmit={formik.handleSubmit}>
        <section className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-10">
          <Input
            type="text"
            label="Contribution name"
            placeholder={"Input contribution name"}
            name="name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
            error={formik?.touched.name && formik.errors.name}
            errorMessage={formik.errors.name}
          />
          <div className="flex flex-col justify-end pb-2">
            <span className="text-sm fw-semibold text-black1 mb-1">Branch</span>
            <span className="text-blacklight">{branchName || "—"}</span>
            <span className="text-xs text-muted mt-1">
              Branch cannot be changed. Create a new category for another
              branch.
            </span>
          </div>
          <Input
            type="number"
            label="Contribution threshold"
            placeholder={"#1,000,000"}
            name="threshold"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.threshold}
            error={formik?.touched.threshold && formik.errors.threshold}
            errorMessage={formik.errors.threshold}
          />
          <Input
            type="date"
            label="Start date"
            placeholder={"Select start date"}
            name="start_date"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.start_date}
            error={formik?.touched.start_date && formik.errors.start_date}
            errorMessage={formik.errors.start_date}
          />
          <Input
            type="date"
            label="End date"
            placeholder={"Select end date"}
            name="end_date"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.end_date}
            error={formik?.touched.end_date && formik.errors.end_date}
            errorMessage={formik.errors.end_date}
          />
          <div>
            <CustomSelect
              options={formattedCompanyBanks}
              styles={customStyles}
              label={"Payment bank"}
              value={selectedBank ?? formik.values.company_bank_id ?? null}
              onChange={(value) => {
                formik.setFieldValue("company_bank_id", value?.value || "");
                formik.setFieldTouched("company_bank_id", true);
              }}
              placeholder="Select bank to accept contribution"
            />
            {formik?.touched.company_bank_id && formik.errors.company_bank_id && (
              <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                {formik.errors.company_bank_id}
              </span>
            )}
          </div>

          <div>
            <CustomSelect
              options={CATEGORY_KIND_OPTIONS}
              styles={customStyles}
              label={"Category type"}
              isDisabled={isSystemCategory}
              onChange={handleKindChange}
              value={
                selectedKind ||
                CATEGORY_KIND_OPTIONS.find(
                  (o) => o.value === formik.values.category_kind
                ) ||
                null
              }
              placeholder="Select category type"
            />
            {formik?.touched.category_kind && formik.errors.category_kind && (
              <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                {formik.errors.category_kind}
              </span>
            )}
          </div>

          <div>
            <CustomSelect
              options={[
                {
                  label: "Withdrawable",
                  value: 1,
                },
                {
                  label: "Non-Withdrawable",
                  value: 0,
                },
              ]}
              styles={customStyles}
              label={"Withdrawable status"}
              isDisabled={kindLocksWithdrawable || isSystemCategory}
              onChange={(selectedOption) => {
                if (kindLocksWithdrawable || isSystemCategory) return;
                setSelectedWithdrawableStatus(selectedOption);
                formik.setFieldValue(
                  "is_withdrawable",
                  selectedOption?.value ?? ""
                );
                formik.setFieldTouched("is_withdrawable", true);
              }}
              value={
                selectedWithdrawableStatus ??
                (formik.values.is_withdrawable === "" ||
                formik.values.is_withdrawable == null
                  ? null
                  : formik.values.is_withdrawable)
              }
              placeholder="Select withdrawable status"
            />
            {(kindLocksWithdrawable || isSystemCategory) && (
              <span className="text-xs text-muted mt-1 d-block">
                Withdrawable status follows category type.
              </span>
            )}
            {formik?.touched.is_withdrawable && formik.errors.is_withdrawable && (
              <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                {formik.errors.is_withdrawable}
              </span>
            )}
          </div>
        </section>
        <div className="my-3 w-full flex justify-center ">
          <button className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white">
            {isLoading ? "Updating category..." : "Update category"}
          </button>
        </div>
      </form>
    </div>
  );
}
